import { validate } from 'app_vodafone_ziggo/util/validation';
import * as debounce from 'lodash/debounce';

/**
 * Initializes the "Let's Talk" functionality for appointment scheduling
 * @param {HTMLElement} modal - The modal element containing the appointment form
 * @returns {Function} A cleanup function to remove event listeners
 */
export default function initLetsTalk(modal) {
    const spinner = document.querySelector('vz-spinner') || new window.VZSpinner();
    const day = modal.querySelector('.js-appointment-day');
    const timeSlot = modal.querySelector('.js-appointment-time');
    const employeesField = modal.querySelector('.js-lead-form-employees');

    // Disable date picker by default
    day.setAttribute('disabled', true);

    /**
     * Resets and disables the time slot selector
     */
    function resetTimeSlot() {
        const selectAppointmentTimeElement = timeSlot.querySelector('.js-select-appointment-time');
        if (selectAppointmentTimeElement) {
            // Remove all options
            [...selectAppointmentTimeElement.options].filter((option) => !option.hasAttribute('disabled')).forEach((option) => option.remove());

            // Reset & disable
            selectAppointmentTimeElement.classList.remove('invalid', 'valid');
            selectAppointmentTimeElement.setAttribute('disabled', true);

            // Remove error message if present
            const timeSlotErrorMessageElement = selectAppointmentTimeElement.parentElement.querySelector('.error-msg');
            if (timeSlotErrorMessageElement) timeSlotErrorMessageElement.remove();
        }
    }

    /**
     * Handles the date change event
     * @param {Event} event - The change event object
     */
    function handleChangeDate(event) {
        const value = `${event.detail.date.getFullYear()}-${event.detail.date.getMonth() + 1}-${event.detail.date.getDate()}`;
        const {
            target: {
                dataset: { url }
            }
        } = event;

        const employeesValue = employeesField.value.trim();
        const urlWithParams = `${url}${value}${employeesValue ? `&employees=${encodeURIComponent(employeesValue)}` : ''}`;
        const dayErrorMessageElement = day.parentElement.querySelector('.error-msg');

        resetTimeSlot();

        // Show spinner
        spinner.start();

        fetch(urlWithParams)
            .then((response) => {
                // Remove day error message if present
                if (response.status === 200 && dayErrorMessageElement) dayErrorMessageElement.remove();

                return response.text();
            })
            .then((response) => {
                timeSlot.innerHTML = response;
            })
            .finally(spinner.stop);
    }

    /**
     * Updates the appointment date section based on employee count
     * @param {string} employeesValue - Number of employees entered
     * @returns {Promise} Promise that resolves when the update is complete
     */
    function handleEmployeesDateUpdate(employeesValue) {
        const dateSection = modal.querySelector('.js-appointment-date');
        const url = employeesField.dataset.url;
        const urlWithParams = `${url}${employeesValue ? `?employees=${encodeURIComponent(employeesValue)}` : ''}`;

        // Show spinner
        spinner.start();

        return fetch(urlWithParams)
            .then((response) => response.text())
            .then((response) => {
                const currentDateField = modal.querySelector('.js-appointment-day');
                const wasDatePickerOpen = currentDateField && currentDateField.parentElement.querySelector('.datepicker.active') !== null;

                dateSection.innerHTML = response;
                // Re-initialize date field since we replaced the content
                const newDateField = dateSection.querySelector('.js-appointment-day');
                if (newDateField) {
                    newDateField.addEventListener('changeDate', handleChangeDate);
                    // Restore focus if the date picker was open before the update
                    if (wasDatePickerOpen) {
                        newDateField.focus();
                    }
                }
            })
            .finally(spinner.stop);
    }

    /**
     * Resets and disables the appointment date field
     */
    function resetAppointmentDate() {
        // Reset appointment date section to initial state
        handleEmployeesDateUpdate('').then(() => {
            const newDateField = modal.querySelector('.js-appointment-day');

            if (newDateField) {
                newDateField.setAttribute('disabled', true);
                newDateField.classList.remove('invalid', 'valid');
                newDateField.classList.add('empty');
                newDateField.value = '';
            }
        });
    }

    /**
     * Checks if the new employee count crosses the threshold in either direction
     * @param {string} oldValue - Previous value from input field
     * @param {string} newValue - New value from input field
     * @param {number} threshold - Threshold value to compare against
     * @returns {boolean} True if threshold is crossed in either direction
     */
    function hasPassedThreshold(oldValue, newValue, threshold) {
        const oldCount = parseInt(oldValue || '0', 10);
        const newCount = parseInt(newValue || '0', 10);

        if (oldCount === 0) return true;

        // Avoid dividing by zero (IFYKYK)
        if (threshold === 0) return false;

        // Did the threshold change
        const wasBelow = oldCount < threshold;
        const isNowBelow = newCount < threshold;

        return wasBelow !== isNowBelow;
    }

    /**
     * Handles employee field changes
     * @param {Event} event - The input event object
     */
    function handleEmployeeChange(event) {
        // Values
        const value = event.target.value.trim();
        const oldValue = event.target.dataset.lastValue || '0';

        // Threshold
        const threshold = parseInt(event.target.dataset.employeeThreshold, 10);
        const surpassedThreshold = hasPassedThreshold(oldValue, value, threshold);

        // If value is empty/invalid don't trigger change
        if (value === '' || Number.isNaN(parseInt(value, 10))) return;

        // If value didn't change don't trigger change
        if (value === oldValue) return;

        // Store current value for next comparison
        event.target.dataset.lastValue = value;

        if (surpassedThreshold) {
            // If the threshold is crossed, update the date field
            if (value) {
                handleEmployeesDateUpdate(value).then(() => {
                    day.removeAttribute('disabled');

                    const newDateField = modal.querySelector('.js-appointment-day');

                    if (newDateField && oldValue !== '0') {
                        validate({ target: newDateField }, newDateField.dataset.surpassThresholdError);
                    }
                });

                resetTimeSlot();
            } else {
                // Reset appointment date section to initial state
                resetAppointmentDate();
                resetTimeSlot();
            }
        } else if (value && value === '0') {
            // User entered zero on purpose, reset appointment date section to initial state
            resetAppointmentDate();
            resetTimeSlot();
        }
    }

    // Debounced event listener
    const debouncedHandleEmployeeChange = debounce(handleEmployeeChange, 500);

    day.addEventListener('changeDate', handleChangeDate);
    employeesField.addEventListener('keyup', debouncedHandleEmployeeChange);

    return () => {
        day.removeEventListener('changeDate', handleChangeDate);
        employeesField.removeEventListener('keyup', debouncedHandleEmployeeChange);

        // Reset last value when destroying the form (closing the modal)
        employeesField.removeAttribute('data-last-value');
    };
}
